import { type ComponentChildren } from 'preact'
import { Route } from 'wouter-preact'
import { Elements } from '@stripe/react-stripe-js'
import {
	loadStripe,
	type Appearance as StripeAppearance,
	type Stripe
} from '@stripe/stripe-js'

import { palette } from 'config'

import { usePayments, PaymentsProvider } from './contexts'
import {
	TeamsPage,
	TeamsPageWithInfo,
	SetupMembers,
	TeamInfoPage,
	SetupTeamPage,
	PaymentDetails
} from './pages'

// Cast Elements to allow Preact usage
const PreactElements = Elements as unknown as preact.FunctionComponent<{
	children: ComponentChildren
	options: {
		clientSecret: string
		appearance?: StripeAppearance
		loader?: 'auto' | 'always' | 'never'
	}
	stripe: PromiseLike<Stripe | null> | Stripe | null
}>

const stripePromise = loadStripe(
	'pk_test_51OkBglLZoy1xcTFMBKHBL7QcBqFFu4V4JGT0y9pjWLzmslgGW1IJJPQg0y4a7PoaVmygzvjv2OaqyPlTp95fsMDI003dd69gwM'
)

const appearance: StripeAppearance = {
	variables: {
		fontFamily: "'Inter', sans-serif",
		fontSizeBase: '14px',
		colorPrimary: palette.product.DEFAULT,
		colorBackground: palette.darkBlack,
		colorText: palette.black[100],
		colorDanger: palette.error.DEFAULT,
		borderRadius: '4px'
		// fontWeightNormal: '400'
	},
	rules: {
		'.Input': {
			backgroundColor: palette.black[800],
			border: `1px solid ${palette.black[500]}`,
			borderRadius: '0.25rem',
			fontFamily: "'Inter', sans-serif",
			fontSize: '14px',
			padding: '10px 10px 10px 16px',
			fontWeight: '600'
		},
		'.Label': {
			color: palette.black[100],
			fontSize: '12px', // '0.75rem'
			lineHeight: '14.4px', // '0.9rem',
			fontWeight: '600',
			marginBottom: '0.5rem'
		},
		'.Input:focus': {
			boxShadow: '0 0 0 1px #FFF',
			outline: 'unset',
			// outlineColor: palette.white,
			borderColor: palette.white
		},
		'.Input::placeholder': {
			color: palette.black[400],
			fontWeight: '300'
		},
		'.Input--invalid': {
			borderColor: palette.error.DEFAULT
		},
		'.Error': {
			fontSize: '12px',
			lineHeight: '14.4px',
			fontWeight: '400',
			color: palette.error.DEFAULT
		},
		'.AccordionItem': {
			border: 'unset'
		}
	}
}

const TeamsRouter = () => {
	const { clientSecret } = usePayments()

	// Enable the skeleton loader UI for optimal loading.
	const loader = 'auto'

	return (
		<>
			<Route component={TeamsPage} path='/' />
			<Route component={TeamsPageWithInfo} path='/teams-with-info' />

			<Route component={SetupTeamPage} path='/setup-team' />
			<Route component={SetupMembers} path='/setup-members' />
			<Route component={TeamInfoPage} path='/team-info' />

			{clientSecret && (
				<PreactElements
					options={{
						clientSecret,
						appearance,
						loader
					}}
					stripe={stripePromise}
				>
					<Route component={PaymentDetails} path='/payment-details' />
				</PreactElements>
			)}
		</>
	)
}

export const TeamsRoutes = () => (
	<PaymentsProvider>
		<TeamsRouter />
	</PaymentsProvider>
)
